import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';
import { X, Save, Bell, Mail, Smartphone, AlertTriangle } from 'lucide-react';
import type { ConfiguracionAlertas } from '../../../shared/types/presupuestos';

interface ConfiguracionAlertasProps {
  configuracion: ConfiguracionAlertas;
  onGuardar: (config: ConfiguracionAlertas) => Promise<void>;
  onCancelar: () => void;
  isOpen: boolean;
  className?: string;
}

export const ConfiguracionAlertas: React.FC<ConfiguracionAlertasProps> = ({
  configuracion,
  onGuardar,
  onCancelar,
  isOpen,
  className = ''
}) => {
  const [formData, setFormData] = useState<ConfiguracionAlertas>({
    emailEnabled: true,
    pushEnabled: true,
    alertaAdvertencia: 80,
    alertaLimite: 100
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setFormData(configuracion);
      setErrors({});
    }
  }, [configuracion, isOpen]);

  const handleInputChange = (field: keyof ConfiguracionAlertas, value: boolean | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validarFormulario = () => {
    const newErrors: Record<string, string> = {};

    if (formData.alertaAdvertencia <= 0 || formData.alertaAdvertencia > 100) {
      newErrors.alertaAdvertencia = 'El porcentaje debe estar entre 1 y 100';
    }

    if (formData.alertaLimite <= 0 || formData.alertaLimite > 200) {
      newErrors.alertaLimite = 'El porcentaje debe estar entre 1 y 200';
    }

    if (formData.alertaAdvertencia >= formData.alertaLimite) {
      newErrors.alertaAdvertencia = 'La advertencia debe ser menor que el límite';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onGuardar(formData);
    } catch (error) {
      console.error('Error guardando configuración:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className={`w-full max-w-md max-h-[90vh] overflow-y-auto ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Bell className="w-5 h-5 mr-2" />
            Configuración de Alertas
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelar}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Canales de notificación */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Canales de Notificación
            </h3>
            
            <div className="space-y-4">
              {/* Email */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <p className="text-sm text-gray-600">
                      Recibir alertas por correo electrónico
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.emailEnabled}
                    onChange={(e) => handleInputChange('emailEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Push Notifications */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Smartphone className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-gray-900">Notificaciones Push</p>
                    <p className="text-sm text-gray-600">
                      Recibir alertas en el navegador
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.pushEnabled}
                    onChange={(e) => handleInputChange('pushEnabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Umbrales de alerta */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Umbrales de Alerta
            </h3>
            
            <div className="space-y-4">
              {/* Alerta de Advertencia */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
                  Advertencia (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.alertaAdvertencia}
                    onChange={(e) => handleInputChange('alertaAdvertencia', parseInt(e.target.value) || 0)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.alertaAdvertencia ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 text-sm">%</span>
                  </div>
                </div>
                {errors.alertaAdvertencia && (
                  <p className="mt-1 text-sm text-red-600">{errors.alertaAdvertencia}</p>
                )}
                <p className="mt-1 text-xs text-gray-600">
                  Se enviará una advertencia cuando el gasto alcance este porcentaje del presupuesto
                </p>
              </div>

              {/* Alerta de Límite */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-500" />
                  Límite crítico (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="200"
                    value={formData.alertaLimite}
                    onChange={(e) => handleInputChange('alertaLimite', parseInt(e.target.value) || 0)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.alertaLimite ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-gray-500 text-sm">%</span>
                  </div>
                </div>
                {errors.alertaLimite && (
                  <p className="mt-1 text-sm text-red-600">{errors.alertaLimite}</p>
                )}
                <p className="mt-1 text-xs text-gray-600">
                  Se enviará una alerta crítica cuando el gasto alcance este porcentaje del presupuesto
                </p>
              </div>
            </div>
          </div>

          {/* Vista previa */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Vista Previa</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span>Advertencia al {formData.alertaAdvertencia}% del presupuesto</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span>Alerta crítica al {formData.alertaLimite}% del presupuesto</span>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={onCancelar}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};